// client/pages/Recoverpassword.tsx
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function RecoverPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "2fa" | "reset">("email");
  const [email, setEmail] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [userId, setUserId] = useState("");
  const [tempUserId, setTempUserId] = useState("");

  const handleBackToLogin = () => {
    navigate("/login");
  };

  async function handleEmailSubmit() {
    if (!email) {
      return toast({
        title: "MISSING EMAIL",
        description: "PLEASE ENTER YOUR EMAIL ADDRESS",
        variant: "destructive",
      });
    }

    setLoading(true);
    try {
      const res = await fetch("/api/check-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      const data = await res.json();

      if (!res.ok || !data.exists) {
        toast({
          title: "USER NOT FOUND",
          description: "No account found with this email address",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (data.has2FA === true) {
        setTempUserId(data.userId);
        setStep("2fa");
        toast({
          title: "2FA REQUIRED",
          description: "Please enter your authentication code to continue",
        });
      } else {
        setUserId(data.userId);
        setStep("reset");
        toast({
          title: "ENTER NEW PASSWORD",
          description: "Set a new password for your account",
        });
      }
    } catch (err: any) {
      toast({
        title: "ERROR",
        description: err?.message || "Failed to verify email",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handle2FASubmit() {
    if (!twoFACode || twoFACode.length !== 6) {
      return toast({
        title: "INVALID CODE",
        description: "PLEASE ENTER A VALID 6-DIGIT AUTHENTICATION CODE",
        variant: "destructive",
      });
    }

    setLoading(true);
    try {
      const res = await fetch("/api/setup-2fa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userId: tempUserId,
          code: twoFACode,
          action: "verify"
        }),
      });

      const data = await res.json();

      if (!data.verified && !data.success) {
        toast({
          title: "INVALID CODE",
          description: data.error || "The authentication code you entered is incorrect",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      setUserId(tempUserId);
      setStep("reset");
      toast({
        title: "2FA VERIFIED",
        description: "Please enter your new password",
      });
    } catch (err: any) {
      console.error("2FA verification error:", err);
      toast({
        title: "ERROR",
        description: err?.message || "Failed to verify authentication code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword() {
    if (!newPassword || !confirmPassword) {
      return toast({
        title: "MISSING FIELDS",
        description: "PLEASE ENTER A NEW PASSWORD",
        variant: "destructive",
      });
    }

    if (newPassword !== confirmPassword) {
      return toast({
        title: "PASSWORD MISMATCH",
        description: "PASSWORDS DO NOT MATCH",
        variant: "destructive",
      });
    }

    if (newPassword.length < 6) {
      return toast({
        title: "WEAK PASSWORD",
        description: "PASSWORD MUST BE AT LEAST 6 CHARACTERS",
        variant: "destructive",
      });
    }

    setLoading(true);
    try {
      const res = await fetch("/api/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userId: userId,
          email: email,
          newPassword: newPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      toast({
        title: "SUCCESS",
        description: "Your password has been reset successfully. Please login with your new password.",
      });
      
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err: any) {
      toast({
        title: "ERROR",
        description: err?.message || "Failed to reset password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url('https://i.postimg.cc/Z5YvQj0Z/pngtree-green-blockchain-bitcoin-fintech-banner-image-196943.jpg')` }}>
      <div className="absolute inset-0 bg-black/60"></div>
      
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 w-full max-w-md mx-auto min-h-screen flex flex-col items-center justify-center px-4">
        <div className="w-full">
          <Card className="bg-black/30 backdrop-blur-sm border-gray-700">
            <CardHeader>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBackToLogin}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div>
                  <CardTitle className="text-white text-lg">RECOVER PASSWORD</CardTitle>
                  <CardDescription className="text-gray-300 text-xs">
                    {step === "email" && "Enter your email address to recover your password"}
                    {step === "2fa" && "Verify your identity with 2FA code"}
                    {step === "reset" && "Set a new password for your account"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {step === "email" && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-300 uppercase tracking-wider">EMAIL</label>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      placeholder="you@example.com"
                      className="bg-gray-900/70 border-gray-600 text-white placeholder:text-gray-400"
                      onKeyPress={(e) => e.key === "Enter" && handleEmailSubmit()}
                    />
                  </div>
                  <Button
                    onClick={handleEmailSubmit}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {loading ? "CHECKING..." : "CONTINUE"}
                  </Button>
                </div>
              )}

              {step === "2fa" && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-300 uppercase tracking-wider">AUTHENTICATION CODE</label>
                    <Input
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ""))}
                      type="text"
                      placeholder="000000"
                      maxLength={6}
                      className="bg-gray-900/70 border-gray-600 text-white placeholder:text-gray-400 text-center text-2xl font-mono"
                      onKeyPress={(e) => e.key === "Enter" && handle2FASubmit()}
                      autoFocus
                    />
                    <p className="text-[8px] text-gray-400 mt-2 text-center">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>
                  <Button
                    onClick={handle2FASubmit}
                    disabled={loading || twoFACode.length !== 6}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {loading ? "VERIFYING..." : "VERIFY & CONTINUE"}
                  </Button>
                </div>
              )}

              {step === "reset" && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-300 uppercase tracking-wider">NEW PASSWORD</label>
                    <div className="relative">
                      <Input
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className="bg-gray-900/70 border-gray-600 text-white placeholder:text-gray-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-2 top-2 text-gray-400 hover:text-white"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-300 uppercase tracking-wider">CONFIRM PASSWORD</label>
                    <div className="relative">
                      <Input
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        type={showConfirm ? "text" : "password"}
                        placeholder="••••••••"
                        className="bg-gray-900/70 border-gray-600 text-white placeholder:text-gray-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((s) => !s)}
                        className="absolute right-2 top-2 text-gray-400 hover:text-white"
                      >
                        {showConfirm ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <Button
                    onClick={handleResetPassword}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {loading ? "RESETTING..." : "SET NEW PASSWORD"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
